import React, { useEffect, useState } from "react";
import Header from "../../../../Componets/Header";
import Loadercomponent from "../../../../Componets/Loader";
import { Modal, Form, Input, Select, DatePicker, Button, message, Table as AntTable } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  add_supplier_balance,
  get_supplier_balance_summary,
  get_supplier_balance_logs
} from "../../../../Controllers/Suppliers/Balance";
import { get_supplier } from "../../../../Controllers/Suppliers/index";
import { get_all_purchases } from "../../../../Controllers/Purchases/Orders";

import headers from "../../../../Data/Tables/Suppliers/Balances/headers";
import ScrollWrapper from "../../../../Componets/ScrollWrapper";

const { Option } = Select;
const { TextArea } = Input;

const SupplierBalanceManagement = () => {
  const id = localStorage.getItem("Supplier_Id");
  const [form] = Form.useForm();
  const [paymentForm] = Form.useForm();

  const [loader, setLoader] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
  const [supplier, setSupplier] = useState(null);
  const [summary, setSummary] = useState({});
  const [outstandingPOs, setOutstandingPOs] = useState([]);
  const [paymentAmounts, setPaymentAmounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [header, setHeader] = useState(headers);

  const transactionTypes = [
    { value: "deposit", label: "Deposit" },
  ];

  const paymentOptions = [
    { value: "Cash", label: "Cash" },
    { value: "Debit card", label: "Debit card" },
    { value: "Bank transfer", label: "Bank transfer" },
    { value: "Credit card", label: "Credit card" },
    { value: "Cheque", label: "Cheque" },
    { value: "Online payment", label: "Online payment" },
  ];

  const headerReset = () => {
    let componentData = [];
    headers?.components?.map((value) => {
      componentData?.push({
        ...value,
        value: null,
      });
    });
    setHeader({ ...headers, components: componentData });
  };

  useEffect(() => {
    if (id) {
      fetchSupplierData();
      fetchBalanceSummary();
      fetchOutstandingPurchaseOrders();
      headerReset();
    }
  }, [id]);

  const fetchSupplierData = async () => {
    try {
      const response = await get_supplier({ id });
      if (response.status) {
        setSupplier(response.data);
      }
    } catch (error) {
      message.error("Failed to fetch supplier data");
    }
  };

  const fetchBalanceSummary = async () => {
    try {
      const response = await get_supplier_balance_summary({ supplier: id });
      if (response.success) {
        setSummary(response.data);
      }
    } catch (error) {
      message.error("Failed to fetch balance summary");
    }
  };

  const calculateOutstanding = (po) => {
    const total = po.total || 0;
    const paid = po.paid_amount || 0;
    return total - paid;
  };

  const fetchOutstandingPurchaseOrders = async (filters = {}) => {
    setLoader(true);
    try {
      const params = {
        supplier: id,
        limit: 100,
        search: filters?.search,
        payment_status: filters?.payment_status,
        date: filters?.date,
      };

      const response = await get_all_purchases(params);

      if (response?.status) {
        const allPOs = response?.data?.data || [];

        const outstanding = allPOs
          .filter((po) => calculateOutstanding(po) > 0)
          .map((po) => ({
            ...po,
            outstanding: calculateOutstanding(po),
          }));

        setOutstandingPOs(outstanding);
        setPaymentAmounts({});
      } else {
        message.error("Failed to fetch purchase orders");
      }
    } catch (error) {
      message.error("Failed to fetch outstanding purchase orders");
    } finally {
      setLoader(false);
    }
  };

  const handleFormData = (data) => {
    fetchOutstandingPurchaseOrders(data);
  };

  const handleAddBalance = async (values) => {
    setLoading(true);
    try {
      const payload = {
        supplier: id,
        type: values.type,
        amount: parseFloat(values.amount),
        description: values.description,
        payment_method: values.payment_method,
        payment_date: values.payment_date
          ? values.payment_date.toDate()
          : new Date(),
        reference_type: "manual",
        reference_number: values.reference_number,
      };

      const response = await add_supplier_balance(payload);

      if (response.success) {
        message.success("Balance transaction added successfully");
        setIsModalVisible(false);
        form.resetFields();
        fetchBalanceSummary();
        fetchOutstandingPurchaseOrders();
      } else {
        message.error(response.message);
      }
    } catch (error) {
      message.error("Failed to add balance transaction");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentAmountChange = (poId, amount) => {
    setPaymentAmounts((prev) => ({
      ...prev,
      [poId]: amount,
    }));
  };

  const handlePayOutstanding = async (values) => {
    setLoading(true);
    try {
      const totalPaymentAmount = Object.values(paymentAmounts).reduce(
        (sum, amount) => sum + (parseFloat(amount) || 0),
        0
      );

      const currentBalance = summary.current_balance || 0;
      
      if (values.type === "withdrawal" && totalPaymentAmount > currentBalance) {
        message.error(
          `Total payment amount (OMR ${totalPaymentAmount.toFixed(
            3
          )}) exceeds current balance (OMR ${currentBalance.toFixed(3)})`
        );
        setLoading(false);
        return;
      }

      if (totalPaymentAmount <= 0) {
        message.error("Please enter valid payment amounts");
        setLoading(false);
        return;
      }

      const poPayload = Object.entries(paymentAmounts)
        .filter(([_, amount]) => amount > 0)
        .map(([poId, amount]) => ({
          purchase_order_id: poId,
          amount: parseFloat(amount),
        }));

      if (poPayload.length === 0) {
        message.error("No valid payment amounts entered");
        setLoading(false);
        return;
      }

      const payload = {
        supplier: id,
        type: "payment",
        amount: parseFloat(totalPaymentAmount),
        description: values.description || "Purchase order payment",
        payment_method: values.payment_method,
        payment_date: values.payment_date
          ? values.payment_date.toDate()
          : new Date(),
        reference_type: "payment",
        reference_number: values.reference_number,
        purchase_orders: poPayload,
      };

      const response = await add_supplier_balance(payload);

      if (response.success) {
        message.success("Payment processed successfully");
        setIsPaymentModalVisible(false);
        paymentForm.resetFields();
        setPaymentAmounts({});
        fetchBalanceSummary();
        fetchOutstandingPurchaseOrders();
      } else {
        message.error(response.message);
      }
    } catch (error) {
      message.error("Failed to process payment");
    } finally {
      setLoading(false);
    }
  };

  const poColumns = [
    {
      title: "PO No",
      dataIndex: "number",
      render: (text) => `#${text}`,
    },
    {
      title: "Date",
      dataIndex: "date",
      render: (date) => moment(date).format("DD-MM-YYYY"),
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      render: (date) => (date ? moment(date).format("DD-MM-YYYY") : "–"),
    },
    {
      title: "Total Amount",
      dataIndex: "total",
      render: (amount) => `OMR ${amount?.toFixed(3)}`,
    },
    {
      title: "Paid Amount",
      dataIndex: "paid_amount",
      render: (paid) => `OMR ${(paid || 0).toFixed(3)}`,
    },
    {
      title: "Outstanding",
      dataIndex: "outstanding",
      render: (outstanding) => `OMR ${outstanding?.toFixed(3)}`,
    },
    {
      title: "Payment Amount",
      dataIndex: "payment_amount",
      render: (_, record) => (
        <Input
          type="number"
          placeholder="0.000"
          min="0"
          max={record.outstanding}
          step="0.001"
          value={paymentAmounts[record._id] || ""}
          onChange={(e) => {
            const value = parseFloat(e.target.value) || 0;
            handlePaymentAmountChange(record._id, value);
          }}
          onBlur={(e) => {
            const value = parseFloat(e.target.value) || 0;
            const outstanding = record.outstanding;
            if (value > outstanding) {
              handlePaymentAmountChange(record._id, outstanding);
              message.warning(
                `Payment amount cannot exceed outstanding amount (OMR ${outstanding.toFixed(
                  3
                )})`
              );
            }
          }}
        />
      ),
    },
  ];

  const getTotalPaymentAmount = () => {
    return Object.values(paymentAmounts).reduce(
      (sum, amount) => sum + (parseFloat(amount) || 0),
      0
    );
  };

  const getCurrentBalance = () => {
    return summary.current_balance || 0;
  };

  const isPaymentValid = () => {
    const totalPayment = getTotalPaymentAmount();
    return totalPayment > 0;
  };

  return (
    <div>
      <Header headers={header} setHeaders={setHeader} formdata={handleFormData}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 pt-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-gray-700 mb-2">Current Balance</h3>
            <p
              className={`${(summary.current_balance || 0) >= 0
                  ? "text-green-600"
                  : "text-red-600"
                }`}
            >
              OMR {summary.current_balance?.toFixed(3) || "0.000"}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-gray-700 mb-2">Total Outstanding</h3>
            <p className=" text-red-600">
              OMR {summary.total_outstanding?.toFixed(3) || "0.000"}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-gray-700 mb-2">Overdue Amount</h3>
            <p className=" text-orange-600">
              OMR {summary.overdue_amount?.toFixed(3) || "0.000"}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-gray-700 mb-2">Available Balance</h3>
            <p
              className={`${(summary.available_balance || 0) >= 0
                  ? "text-blue-600"
                  : "text-red-600"
                }`}
            >
              OMR {summary.available_balance?.toFixed(3) || "0.000"}
            </p>
          </div>
        </div>

        <div className="flex gap-4 mb-6">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsModalVisible(true)}
          >
            Add Transaction
          </Button>
          <Button
            type="default"
            onClick={() => setIsPaymentModalVisible(true)}
            disabled={summary.total_outstanding <= 0}
          >
            Pay Outstanding POs
          </Button>
        </div>

        <Loadercomponent loader={loader}>
           <ScrollWrapper summary={1.8}>
            <AntTable
              columns={poColumns}
              dataSource={outstandingPOs}
              rowKey="_id"
              pagination={false}
              size="middle"
            />
           </ScrollWrapper>
        </Loadercomponent>
      </Header>

      <Modal
        title="Add Supplier Balance Transaction"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical" onFinish={handleAddBalance}>
          <Form.Item
            name="type"
            label="Transaction Type"
            rules={[{ required: true, message: "Please select type" }]}
          >
            <Select>
              {transactionTypes.map((type) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Amount"
            rules={[{ required: true, message: "Please enter amount" }]}
          >
            <Input type="number" step="0.001" placeholder="0.000" min="0" />
          </Form.Item>

          <Form.Item
            name="payment_method"
            label="Payment Method"
            rules={[
              { required: true, message: "Please select payment method" },
            ]}
          >
            <Select>
              {paymentOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="reference_number" label="Reference Number">
            <Input placeholder="Enter reference number" />
          </Form.Item>

          <Form.Item
            name="payment_date"
            label="Transaction Date"
            initialValue={moment()}
          >
            <DatePicker format="DD-MM-YYYY" className="w-full" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Enter description" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Pay Outstanding Purchase Orders"
        open={isPaymentModalVisible}
        onCancel={() => {
          setIsPaymentModalVisible(false);
          paymentForm.resetFields();
          setPaymentAmounts({});
        }}
        onOk={() => paymentForm.submit()}
        confirmLoading={loading}
        width={800}
        okButtonProps={{ disabled: !isPaymentValid() }}
      >
        <Form
          form={paymentForm}
          layout="vertical"
          onFinish={handlePayOutstanding}
        >
          <Form.Item
            name="payment_method"
            label="Payment Method"
            rules={[
              { required: true, message: "Please select payment method" },
            ]}
          >
            <Select>
              {paymentOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="reference_number" label="Reference Number">
            <Input placeholder="Enter reference number" />
          </Form.Item>

          <Form.Item name="payment_date" label="Payment Date" /* initialValue={moment()} */>
            <DatePicker format="DD-MM-YYYY" className="w-full" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Enter payment description" />
          </Form.Item>

          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-semibold">Payment Summary:</h4>
              <div className="text-sm">
                <span className="text-gray-600">Current Balance: </span>
                <span className="font-semibold">
                  OMR {getCurrentBalance().toFixed(3)}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Payment Amount:</span>
              <span
                className={`font-semibold ${getTotalPaymentAmount() > 0
                    ? "text-green-600"
                    : "text-gray-600"
                  }`}
              >
                OMR {getTotalPaymentAmount().toFixed(3)}
              </span>
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default SupplierBalanceManagement;